import { QuantitySelectorUpdateEvent } from '@theme/events';

class CodPackSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    if (!this.#hydrateMirror()) return;
    this.dataset.ready = 'true';
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onPackClick);
    this.querySelector('[data-cod-submit]')?.addEventListener('click', this.#submit);
    document.addEventListener('shopify:product:select', this.#restoreQuantity);

    const selected = this.querySelector('input[data-pack-quantity]:checked');
    if (selected) this.#sync(selected);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:product:select', this.#restoreQuantity);
  }

  #onChange = (event) => {
    const input = event.target.closest('input[data-pack-quantity]');
    if (input) this.#sync(input, true);
  };

  #onPackClick = (event) => {
    const input = event.target.closest('input[data-pack-quantity]');
    if (input?.checked) this.#sync(input, true);
  };

  #restoreQuantity = (event) => {
    event.promise?.finally(() => {
      const selected = document.querySelector(
        `cod-pack-selector[data-product-id="${this.dataset.productId}"] input[data-pack-quantity]:checked`
      );
      if (selected) this.#sync(selected);
    });
  };

  #hydrateMirror() {
    if (this.dataset.codPackRole !== 'mirror') return true;

    const source = document.querySelector(
      `cod-pack-selector[data-product-id="${this.dataset.productId}"][data-cod-pack-role="source"]`
    );
    const sharedContent = source?.querySelector('[data-cod-pack-shared-content]');
    const mirrorContent = this.querySelector('[data-cod-pack-mirror-content]');
    if (!sharedContent || !mirrorContent) return false;

    const clone = sharedContent.cloneNode(true);
    clone.removeAttribute('data-cod-pack-shared-content');
    clone.querySelectorAll('input[data-pack-quantity]').forEach((input) => {
      input.name = `cod-pack-${this.dataset.codPackInstance}`;
    });
    mirrorContent.replaceChildren(...clone.childNodes);
    this.hidden = false;
    return true;
  }

  #sync(source, explicitSelection = false) {
    const productId = this.dataset.productId;
    const quantity = source.dataset.packQuantity;
    const displayPrice = source.dataset.packPrice;
    const sourceSelector = document.querySelector(
      `cod-pack-selector[data-product-id="${productId}"][data-cod-pack-role="source"]`
    );
    if (explicitSelection) sourceSelector.dataset.codSelectionExplicit = 'true';
    const hasExplicitSelection = sourceSelector?.dataset.codSelectionExplicit === 'true';

    document.querySelectorAll(`cod-pack-selector[data-product-id="${productId}"]`).forEach((selector) => {
      selector.querySelectorAll('input[data-pack-quantity]').forEach((input) => {
        input.checked = input.dataset.packQuantity === quantity;
        input.closest('.cod-pack-card')?.classList.toggle('is-selected', input.checked);
      });
    });

    document.querySelectorAll(`product-form-component[data-product-id="${productId}"] form`).forEach((form) => {
      let input = form.querySelector('input[name="quantity"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.dataset.codPackQuantity = 'true';
        form.append(input);
      }
      input.value = quantity;
      form.closest('product-form-component').dataset.quantityDefault = quantity;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    document.dispatchEvent(new QuantitySelectorUpdateEvent(Number(quantity)));

    document.querySelectorAll('[data-cod-pack-price]').forEach((price) => {
      if (price.closest('sticky-add-to-cart')) return;
      price.textContent = displayPrice;
      price.hidden = false;
    });
    document.querySelectorAll('[data-cod-native-price]').forEach((price) => {
      if (!price.closest('sticky-add-to-cart')) price.hidden = true;
    });
    const purchaseLabel = this.#updatePurchaseCtas(productId, quantity, displayPrice);
    this.#updateSticky(productId, quantity, displayPrice, hasExplicitSelection, purchaseLabel);
  }

  #updatePurchaseCtas(productId, quantity, displayPrice) {
    document
      .querySelectorAll(
        `cod-pack-selector[data-product-id="${productId}"] [data-cod-submit]`
      )
      .forEach((button) => {
        const baseLabel = button.dataset.codBaseLabel || button.textContent.trim();
        button.dataset.codBaseLabel = baseLabel;
        const ctaText = `${baseLabel} ${quantity} · ${displayPrice}`;
        if (!button.disabled) button.textContent = ctaText;
      });

    const mainButton = document.querySelector(
      `product-form-component[data-product-id="${productId}"] [ref="addToCartButton"]`
    );
    const labelNode = Array.from(mainButton?.childNodes ?? []).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );
    const baseLabel = mainButton?.dataset.codBaseLabel || labelNode?.textContent.trim() || 'COMPRAR';
    if (mainButton) mainButton.dataset.codBaseLabel = baseLabel;
    const ctaText = `${baseLabel} ${quantity} · ${displayPrice}`;
    if (mainButton && labelNode) {
      if (!mainButton.disabled) labelNode.textContent = ` ${ctaText} `;
    }
    return baseLabel;
  }

  #updateSticky(productId, quantity, displayPrice, hasExplicitSelection, purchaseLabel) {
    const sticky = document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
    if (!sticky) return;

    const priceContainer = sticky.querySelector('.sticky-add-to-cart__price');
    const nativePrice = sticky.querySelector('[data-cod-native-price]');
    const packPrice = sticky.querySelector('[data-cod-pack-price]');
    const label = sticky.querySelector('[data-cod-sticky-label]');
    const productLabel = sticky.querySelector('[data-cod-sticky-product-label]');
    const meta = sticky.querySelector('[data-cod-sticky-meta]');
    const quantityDisplay = sticky.querySelector('[ref="quantityDisplay"]');
    const quantityNumber = sticky.querySelector('[ref="quantityNumber"]');

    sticky.dataset.codPackSelected = String(hasExplicitSelection);
    if (!hasExplicitSelection) {
      if (priceContainer) {
        priceContainer.hidden = true;
        priceContainer.style.display = 'none';
      }
      if (nativePrice) nativePrice.hidden = false;
      if (packPrice) packPrice.hidden = true;
      if (label) label.textContent = sticky.dataset.codDefaultCtaLabel || 'QUIERO COMPRAR';
      if (productLabel) productLabel.textContent = productLabel.dataset.codBaseLabel;
      if (meta) meta.textContent = `Desde ${meta.dataset.codDefaultPrice}`;
      if (quantityDisplay) quantityDisplay.style.display = 'none';
      return;
    }

    if (priceContainer) {
      priceContainer.hidden = false;
      priceContainer.style.removeProperty('display');
    }
    if (nativePrice) nativePrice.hidden = true;
    if (packPrice) {
      packPrice.textContent = displayPrice;
      packPrice.hidden = false;
    }
    if (label) label.textContent = purchaseLabel || 'COMPRAR';
    if (productLabel) {
      const unitLabel = quantity === '1' ? 'UNIDAD' : 'UNIDADES';
      productLabel.textContent = `${productLabel.dataset.codBaseLabel} · ${quantity} ${unitLabel}`;
    }
    if (meta) meta.textContent = displayPrice;
    if (quantityNumber) quantityNumber.textContent = quantity;
    if (quantityDisplay) quantityDisplay.style.display = 'none';
  }

  #submit = () => {
    const productId = this.dataset.productId;
    const selected = this.querySelector('input[data-pack-quantity]:checked');
    if (selected) this.#sync(selected, true);
    const button = document.querySelector(
      `product-form-component[data-product-id="${productId}"] [ref="addToCartButton"]`
    );
    button?.click();
  };
}

if (!customElements.get('cod-pack-selector')) {
  customElements.define('cod-pack-selector', CodPackSelector);
}

class CodPromoCountdown extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.endAt = Date.parse(this.dataset.endAt);
    if (!Number.isFinite(this.endAt)) return;

    this.hours = this.querySelector('[data-countdown-hours]');
    this.minutes = this.querySelector('[data-countdown-minutes]');
    this.seconds = this.querySelector('[data-countdown-seconds]');
    this.label = this.querySelector('[data-countdown-label]');
    this.time = this.querySelector('[data-countdown-time]');
    this.expired = this.querySelector('[data-countdown-expired]');
    this.#update();

    if (this.endAt > Date.now()) this.interval = window.setInterval(() => this.#update(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
  }

  #update() {
    const remainingSeconds = Math.max(0, Math.floor((this.endAt - Date.now()) / 1000));
    if (remainingSeconds <= 0) {
      window.clearInterval(this.interval);
      if (this.dataset.expiredBehavior === 'message' && this.expired?.textContent.trim()) {
        this.label.hidden = true;
        this.time.hidden = true;
        this.expired.hidden = false;
        this.hidden = false;
      } else {
        this.hidden = true;
      }
      return;
    }

    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    this.hours.textContent = String(hours).padStart(2, '0');
    this.minutes.textContent = String(minutes).padStart(2, '0');
    this.seconds.textContent = String(seconds).padStart(2, '0');
    this.hidden = false;
  }
}

if (!customElements.get('cod-promo-countdown')) {
  customElements.define('cod-promo-countdown', CodPromoCountdown);
}

class CodLiveVisitors extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.hidden = true;
    document.addEventListener('cod:live-visitors:update', this.#onUpdate);

    const currentCount = window.CodLiveVisitors?.counts.get(String(this.dataset.productId));
    if (currentCount !== undefined) this.#render(currentCount);
  }

  disconnectedCallback() {
    document.removeEventListener('cod:live-visitors:update', this.#onUpdate);
  }

  #onUpdate = (event) => {
    if (String(event.detail?.productId) !== String(this.dataset.productId)) return;
    this.#render(event.detail?.count);
  };

  #render(value) {
    const count = Number(value);
    const minimumCount = Number(this.dataset.minCount) || 1;
    if (!Number.isFinite(count) || count < minimumCount) {
      this.hidden = true;
      return;
    }

    const message = this.dataset.messageTemplate.replaceAll('[[count]]', String(Math.floor(count)));
    this.querySelector('[data-live-visitors-message]').textContent = message;
    this.hidden = false;
  }
}

if (!window.CodLiveVisitors) {
  window.CodLiveVisitors = {
    counts: new Map(),
    update(productId, count) {
      this.counts.set(String(productId), count);
      document.dispatchEvent(new CustomEvent('cod:live-visitors:update', { detail: { productId, count } }));
    },
  };
}

if (!customElements.get('cod-live-visitors')) {
  customElements.define('cod-live-visitors', CodLiveVisitors);
}

const initCodCampaign = (root = document) => {
  const revealItems = root.querySelectorAll?.('.cod-reveal:not([data-cod-reveal-ready])') ?? [];

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  } else {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );
    revealItems.forEach((item) => {
      item.dataset.codRevealReady = 'true';
      observer.observe(item);
    });
  }
};

if (!window.codCampaignEventsReady) {
  window.codCampaignEventsReady = true;
  const scrollToPacks = (productId) => {
    document
      .querySelector(`cod-pack-selector[data-product-id="${productId}"][data-cod-pack-role="source"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  document.addEventListener(
    'click',
    (event) => {
      const stickyButton = event.target.closest('sticky-add-to-cart [ref="addToCartButton"]');
      if (!stickyButton) return;
      const sticky = stickyButton.closest('sticky-add-to-cart');
      if (sticky.dataset.codPackSelected === 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToPacks(sticky.dataset.productId);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cod-scroll-to-packs]');
    if (!trigger) return;
    event.preventDefault();
    scrollToPacks(trigger.dataset.productId);
  });
  document.addEventListener('shopify:section:load', (event) => initCodCampaign(event.target));
}

initCodCampaign();
